/**
 * useWindowedSlice — list windowing that leaves the DOM ordinary (D-9).
 *
 * WHY NOT react-window HERE. Two of the three lists this serves scroll with the
 * PAGE (Conversations cards, the Contacts table), and the third is a real
 * `<table>`. react-window owns its own scroll container and renders
 * absolutely-positioned divs, so using it would mean either bolting on the
 * window-scroller trick (a synthetic scrollbar, changed keyboard/anchor
 * behaviour) or rebuilding the table as divs. Both are visible changes, and this
 * package's bar is ZERO visible change. So instead: keep every row exactly as it
 * is, render only the visible range, and hold the scrollbar honest with two
 * spacer elements the consumer supplies in its own markup language (`<li>`,
 * `<tr>`, `<div>`).
 *
 * BELOW THE THRESHOLD (default 100 rows) the hook does NOTHING — `enabled` is
 * false, no listeners are attached, no spacers are rendered, and the consumer
 * renders precisely the DOM it rendered before this package existed.
 *
 * PITCH, NOT HEIGHT. Rows are measured as the distance between consecutive row
 * tops, so a container's own gap (`space-y-2`) is included automatically. The
 * pads therefore subtract one gap: the first rendered row still gets its
 * inherited margin from the spacer above it.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Row count at which windowing switches on. Below it, nothing changes. */
export const WINDOW_THRESHOLD = 100;
/** Rows rendered beyond each edge — keyboard focus and fast scrolls land on
 *  real DOM instead of a spacer. */
export const DEFAULT_OVERSCAN = 8;

export interface SliceRange {
  start: number;
  end: number;
}

/**
 * Pure range math (unit-tested directly — no DOM).
 *
 * `offset` is how far the list's top has scrolled past the viewport's top,
 * `viewport` the visible height, `pitch` the per-row stride INCLUDING any gap.
 */
export function computeSliceRange(args: {
  count: number;
  pitch: number;
  offset: number;
  viewport: number;
  overscan?: number;
}): SliceRange {
  const { count } = args;
  const overscan = args.overscan ?? DEFAULT_OVERSCAN;
  const pitch = args.pitch > 0 ? args.pitch : 1;
  const viewport = Math.max(0, args.viewport);
  const offset = Math.max(0, args.offset);
  if (count <= 0) return { start: 0, end: 0 };
  const firstVisible = Math.floor(offset / pitch);
  const lastVisible = Math.ceil((offset + viewport) / pitch);
  const start = Math.min(Math.max(0, firstVisible - overscan), count);
  const end = Math.min(Math.max(start, lastVisible + overscan), count);
  return { start, end };
}

/** Row pitch from the DOM: the mean distance between rendered row tops, with
 *  spacers (marked `data-spacer`) excluded. Null when nothing is measurable. */
export function measurePitch(container: HTMLElement, gap: number): number | null {
  const rows = Array.from(container.children).filter(
    (el) => !(el as HTMLElement).dataset?.spacer,
  ) as HTMLElement[];
  if (rows.length === 0) return null;
  if (rows.length === 1) {
    const h = rows[0].offsetHeight;
    return h > 0 ? h + gap : null;
  }
  const span = rows[rows.length - 1].offsetTop - rows[0].offsetTop;
  if (span > 0) return span / (rows.length - 1);
  const h = rows[0].offsetHeight;
  return h > 0 ? h + gap : null;
}

export interface WindowedSliceOptions {
  /** Total rows in the data set (NOT the rendered count). */
  count: number;
  /** 'window' = the page scrolls (default); 'self' = the container scrolls. */
  scroller?: 'window' | 'self';
  /** Vertical gap the container adds between rows (`space-y-2` → 8). */
  gap?: number;
  /** Stride used until the first measurement lands. */
  estimatedRowHeight?: number;
  threshold?: number;
  overscan?: number;
}

export interface WindowedSlice {
  /** False = render every row exactly as before (no spacers, no listeners). */
  enabled: boolean;
  /**
   * Attach to the element whose children are the rows (`<ul>`, `<tbody>`, the
   * card stack). A CALLBACK ref, deliberately: React invokes it exactly when
   * the element attaches or detaches, which is the only reliable signal that a
   * list rendered behind a tab (or below a loading spinner) has arrived. A
   * RefObject mutation cannot wake an effect, so the scroll listener would
   * never arm for those lists.
   */
  containerRef: (el: HTMLElement | null) => void;
  start: number;
  end: number;
  /** Height of the spacer standing in for rows [0, start). 0 = render none. */
  topPad: number;
  /** Height of the spacer standing in for rows [end, count). 0 = render none. */
  bottomPad: number;
}

export function useWindowedSlice(options: WindowedSliceOptions): WindowedSlice {
  const {
    count,
    scroller = 'window',
    gap = 0,
    estimatedRowHeight = 96,
    threshold = WINDOW_THRESHOLD,
    overscan = DEFAULT_OVERSCAN,
  } = options;

  const enabled = count > threshold;
  const [pitch, setPitch] = useState(estimatedRowHeight + gap);
  const pitchRef = useRef(pitch);
  const [range, setRange] = useState<SliceRange>({ start: 0, end: 0 });
  const rangeRef = useRef(range);
  const frameRef = useRef<number | null>(null);
  // The container as BOTH a ref (read synchronously while measuring) and state
  // (so its arrival re-runs the effects). The callback ref below writes both.
  const nodeRef = useRef<HTMLElement | null>(null);
  const [node, setNode] = useState<HTMLElement | null>(null);
  const containerRef = useCallback((el: HTMLElement | null) => {
    nodeRef.current = el;
    setNode(el);
  }, []);
  // The row count the current pitch was sampled for. Measuring exactly ONCE per
  // row-set (rather than after every render) is what makes this terminate on
  // variable-height rows: re-measuring on every pass would let a new pitch pick
  // a new window, whose different rows yield a new pitch, forever.
  const pitchSampledForRef = useRef(-1);

  /** Recompute the visible range from the live scroll position. */
  const measure = useCallback(() => {
    const el = nodeRef.current;
    if (!el) return;
    let offset: number;
    let viewport: number;
    if (scroller === 'self') {
      offset = el.scrollTop;
      viewport = el.clientHeight;
    } else {
      const rect = el.getBoundingClientRect();
      offset = Math.max(0, -rect.top);
      viewport = typeof window !== 'undefined' ? window.innerHeight : 0;
    }
    const next = computeSliceRange({
      count,
      pitch: pitchRef.current,
      offset,
      viewport,
      overscan,
    });
    const prev = rangeRef.current;
    if (next.start === prev.start && next.end === prev.end) return;
    rangeRef.current = next;
    setRange(next);
  }, [count, overscan, scroller]);

  // Scroll/resize are rAF-throttled: a fling fires dozens of events per second
  // and only the last position of each frame can matter.
  const schedule = useCallback(() => {
    if (frameRef.current !== null) return;
    const run = () => {
      frameRef.current = null;
      measure();
    };
    frameRef.current = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame(run)
      : window.setTimeout(run, 16);
  }, [measure]);

  // A resize can change every row's height (responsive rows) — invalidate the
  // sample so the next pass re-measures.
  const onResize = useCallback(() => {
    pitchSampledForRef.current = -1;
    schedule();
  }, [schedule]);

  useEffect(() => {
    if (!enabled) return undefined;
    const target: EventTarget | null = scroller === 'self' ? node : window;
    if (!target) return undefined;
    target.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', onResize);
    // The list can also change size without a scroll or a resize: a `hidden`
    // container being revealed (the console's master list on a phone), or rows
    // reflowing. Observing the box covers both.
    let observer: ResizeObserver | null = null;
    if (node && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => { onResize(); });
      observer.observe(node);
    }
    return () => {
      target.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', onResize);
      observer?.disconnect();
      if (frameRef.current !== null) {
        if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frameRef.current);
        else window.clearTimeout(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [enabled, node, scroller, schedule, onResize]);

  // Runs after every windowed render (pre-paint): re-derive the range from the
  // live scroll position — which is what keeps a filter change, a "Load more"
  // or a pitch update honest without waiting for a scroll event that will never
  // come — and sample the pitch once per row-set. Both writes are guarded, so
  // this converges in a couple of passes instead of looping.
  useLayoutEffect(() => {
    const el = nodeRef.current;
    if (!enabled || !el) return;
    if (range.end > range.start && pitchSampledForRef.current !== count) {
      const measured = measurePitch(el, gap);
      if (measured !== null) {
        pitchSampledForRef.current = count;
        if (Math.abs(measured - pitch) > 1) {
          pitchRef.current = measured;
          setPitch(measured);
        }
      }
    }
    measure();
  }, [enabled, count, gap, measure, node, pitch, range]);

  if (!enabled) {
    return { enabled: false, containerRef, start: 0, end: count, topPad: 0, bottomPad: 0 };
  }
  const start = Math.min(range.start, count);
  const end = Math.min(Math.max(range.end, start), count);
  return {
    enabled: true,
    containerRef,
    start,
    end,
    // One `gap` comes back from the row that follows/precedes the spacer, which
    // keeps its inherited margin.
    topPad: start > 0 ? Math.max(0, start * pitch - gap) : 0,
    bottomPad: end < count ? Math.max(0, (count - end) * pitch - gap) : 0,
  };
}

export default useWindowedSlice;
